import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { BrandedHeaderMenuContainerContext } from '@repo/ui/BrandedHeader/menuContainer';

import { useWorkflow } from '../../workflow/hooks';
import { BrandedHeader } from '../BrandedHeader/BrandedHeader';
import { NarvarFooter } from '../NarvarFooter/NarvarFooter';

import s from './Page.module.css';
import { useRetailerSettings } from '../../hooks/useRetailerSettings';

interface PageWrapProps {
  children: ReactNode;
  /**
   * Whether the branded Narvar footer closes the page. The pages that navigate within
   * themselves keep it; a page whose footer region is its navigation buttons — an authored
   * page that carries no `footer` block — drops it, or the buttons would sit on top of it.
   */
  brandedFooter?: boolean;
}

interface PageProps extends PageWrapProps {
  isPublicRoute?: boolean;
}

export const PageWrap = ({ children, brandedFooter = true }: PageWrapProps) => {
  const { isError, isLoading } = useRetailerSettings();
  const [menuContainer, setMenuContainer] = useState<HTMLDivElement | null>(
    null,
  );
  return (
    <BrandedHeaderMenuContainerContext.Provider value={menuContainer}>
      <div ref={setMenuContainer} className={s.container}>
        {!isError && !isLoading && <BrandedHeader />}
        {children}
        {brandedFooter && <NarvarFooter />}
      </div>
    </BrandedHeaderMenuContainerContext.Provider>
  );
};

export const Page = (props: PageProps) => {
  const { children, brandedFooter } = props;
  const navigate = useNavigate();
  const { isAuthenticated } = useWorkflow();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate({ to: '/', replace: true, search: true });
    }
  }, [isAuthenticated, navigate]);

  return <PageWrap brandedFooter={brandedFooter}>{children}</PageWrap>;
};
